import { Button } from "@designs-by-maryam/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
} from "@designs-by-maryam/ui/components/dialog";
import { Skeleton } from "@designs-by-maryam/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowLeft,
	Check,
	Download,
	File,
	FileImage,
	FileText,
	FolderOpen,
	Image,
	X,
} from "lucide-react";
import { useState } from "react";
import { requireClient } from "@/functions/require-role";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_dashboard/portal/projects/$id")({
	component: ProjectDetail,
	beforeLoad: async () => {
		await requireClient();
	},
});

type WorkflowStatus =
	| "planning"
	| "design"
	| "approval"
	| "implementation"
	| "completed";

const WORKFLOW_STAGES: {
	id: WorkflowStatus;
	label: string;
	description: string;
	color: string;
}[] = [
	{
		id: "planning",
		label: "Planning",
		description: "Initial consultation and requirements gathering",
		color: "bg-gray-500",
	},
	{
		id: "design",
		label: "Design",
		description: "Concept development and 3D visualization",
		color: "bg-blue-500",
	},
	{
		id: "approval",
		label: "Approval",
		description: "Client review and design approval",
		color: "bg-yellow-500",
	},
	{
		id: "implementation",
		label: "Implementation",
		description: "Construction and installation",
		color: "bg-purple-500",
	},
	{
		id: "completed",
		label: "Completed",
		description: "Project delivered and finalized",
		color: "bg-green-500",
	},
];

type FileCategory =
	| "design"
	| "contract"
	| "invoice"
	| "inspiration"
	| "floorplan"
	| "other";

const FILE_CATEGORY_CONFIG: Record<
	FileCategory,
	{ label: string; icon: typeof File }
> = {
	design: { label: "Designs", icon: FileImage },
	contract: { label: "Contracts", icon: FileText },
	invoice: { label: "Invoices", icon: FileText },
	inspiration: { label: "Inspiration", icon: Image },
	floorplan: { label: "Floor Plans", icon: File },
	other: { label: "Other", icon: File },
};

function getStageClassName(isActive: boolean, isCompleted: boolean): string {
	if (isActive) {
		return "border-primary bg-primary/5 dark:border-primary dark:bg-primary/10";
	}
	if (isCompleted) {
		return "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/20";
	}
	return "border-border bg-card opacity-60";
}

function getIndicatorColor(
	isCompleted: boolean,
	isActive: boolean,
	stageColor: string,
): string {
	if (isCompleted) {
		return "bg-green-500";
	}
	if (isActive) {
		return stageColor;
	}
	return "bg-muted-foreground/30";
}

function ProjectDetail() {
	const { id } = Route.useParams();
	const [activeTab, setActiveTab] = useState<"overview" | "files">("overview");
	const [fileFilter, setFileFilter] = useState<FileCategory | "all">("all");
	const [lightboxImage, setLightboxImage] = useState<string | null>(null);

	const { data: project, isLoading: projectLoading } = useQuery(
		orpc.portalGetProjectDetails.queryOptions({
			input: { clientProjectId: id },
		}),
	);

	const { data: files, isLoading: filesLoading } = useQuery(
		orpc.portalGetProjectFiles.queryOptions({ input: { clientProjectId: id } }),
	);

	const currentStageIndex = WORKFLOW_STAGES.findIndex(
		(s) => s.id === project?.workflowStatus,
	);

	const filteredFiles =
		fileFilter === "all"
			? files
			: files?.filter((f) => f.category === fileFilter);

	if (projectLoading) {
		return <ProjectDetailSkeleton />;
	}

	if (!project) {
		return (
			<div className="container mx-auto px-4 py-8">
				<div className="rounded-lg border bg-card p-12 text-center">
					<FolderOpen className="mx-auto h-12 w-12 text-muted-foreground" />
					<h3 className="mt-4 font-semibold text-lg">Project not found</h3>
					<Link to="/portal">
						<Button className="mt-4" variant="outline">
							<ArrowLeft className="mr-2 h-4 w-4" />
							Back to Dashboard
						</Button>
					</Link>
				</div>
			</div>
		);
	}

	return (
		<div className="container mx-auto px-4 py-8 md:px-6">
			{/* Back Button */}
			<Link
				className="mb-6 inline-flex items-center text-muted-foreground hover:text-foreground"
				to="/portal"
			>
				<ArrowLeft className="mr-2 h-4 w-4" />
				Back to Dashboard
			</Link>
			{/* Header */}
			<div className="mb-8">
				<div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
					<div>
						<h1 className="font-bold text-2xl md:text-3xl">{project.title}</h1>
						{project.category && (
							<p className="mt-1 text-muted-foreground capitalize">
								{project.category} • {project.style || "Modern"}
							</p>
						)}
					</div>
					<div className="flex items-center gap-2">
						<span className="font-medium text-2xl">
							{project.progressPercentage}%
						</span>
						<span className="text-muted-foreground">complete</span>
					</div>
				</div>
				{project.description && (
					<p className="mt-4 max-w-3xl text-muted-foreground">
						{project.description}
					</p>
				)}
			</div>
			{/* Tabs */}
			<div
				aria-label="Project sections"
				className="mb-6 flex border-b"
				role="tablist"
			>
				<button
					aria-controls="tabpanel-overview"
					aria-selected={activeTab === "overview"}
					className={`px-4 py-2 font-medium text-sm transition-colors ${
						activeTab === "overview"
							? "border-primary border-b-2 text-primary"
							: "text-muted-foreground hover:text-foreground"
					}`}
					id="tab-overview"
					onClick={() => setActiveTab("overview")}
					role="tab"
					type="button"
				>
					Overview
				</button>
				<button
					aria-controls="tabpanel-files"
					aria-selected={activeTab === "files"}
					className={`px-4 py-2 font-medium text-sm transition-colors ${
						activeTab === "files"
							? "border-primary border-b-2 text-primary"
							: "text-muted-foreground hover:text-foreground"
					}`}
					id="tab-files"
					onClick={() => setActiveTab("files")}
					role="tab"
					type="button"
				>
					Files ({files?.length || 0})
				</button>
			</div>
			{activeTab === "overview" ? (
				<>
					{/* Kanban Board */}
					<div className="mb-8">
						<h2 className="mb-4 font-semibold text-lg">Project Timeline</h2>
						<div className="overflow-x-auto">
							<div className="flex min-w-[800px] gap-3">
								{WORKFLOW_STAGES.map((stage, index) => {
									const isActive = stage.id === project.workflowStatus;
									const isCompleted = index < currentStageIndex;
									const isPending = index > currentStageIndex;

									return (
										<div
											className={`relative flex-1 rounded-lg border p-4 ${getStageClassName(isActive, isCompleted)}`}
											key={stage.id}
										>
											{/* Connector Line */}
											{index < WORKFLOW_STAGES.length - 1 && (
												<div
													className={`-right-4 absolute top-1/2 h-0.5 w-4 ${
														isCompleted || isActive ? "bg-primary" : "bg-border"
													}`}
												/>
											)}

											{/* Status Indicator */}
											<div className="mb-3 flex items-center gap-2">
												<div
													className={`flex h-6 w-6 items-center justify-center rounded-full text-white text-xs ${getIndicatorColor(isCompleted, isActive, stage.color)}`}
												>
													{isCompleted ? (
														<Check className="h-4 w-4" />
													) : (
														index + 1
													)}
												</div>
												<span
													className={`font-medium text-sm ${
														isActive ? "text-primary" : ""
													}`}
												>
													{stage.label}
												</span>
											</div>

											<p
												className={`text-xs ${
													isPending
														? "text-muted-foreground/60"
														: "text-muted-foreground"
												}`}
											>
												{stage.description}
											</p>

											{isActive && project.nextAction && (
												<div className="mt-3 rounded bg-primary/10 p-2 dark:bg-primary/20">
													<p className="font-medium text-primary text-xs dark:text-primary">
														Next: {project.nextAction}
													</p>
												</div>
											)}
										</div>
									);
								})}
							</div>
						</div>
					</div>

					{/* Project Images */}
					{project.images && project.images.length > 0 && (
						<div>
							<h2 className="mb-4 font-semibold text-lg">Project Gallery</h2>
							<div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
								{(project.images as Array<{ url: string; alt?: string }>).map(
									(img, i) => {
										const previewImage = (
											// biome-ignore lint/a11y/noNoninteractiveElementInteractions: onError handles image fallback.
											<img
												alt={img.alt || `Project image ${i + 1}`}
												className="h-full w-full object-cover transition-transform group-hover:scale-105"
												height={200}
												loading="lazy"
												onError={(e) => {
													e.currentTarget.style.display = "none";
												}}
												src={img.url}
												width={200}
											/>
										);

										return (
											<button
												className="group relative aspect-square overflow-hidden rounded-lg"
												key={img.url}
												onClick={() => setLightboxImage(img.url)}
												type="button"
											>
												{previewImage}
												<div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/30">
													<Image className="h-6 w-6 text-white opacity-0 transition-opacity group-hover:opacity-100" />
												</div>
											</button>
										);
									},
								)}
							</div>
						</div>
					)}
				</>
			) : (
				/* Files Tab */
				<div>
					{/* File Filters */}
					<div className="mb-4 flex flex-wrap gap-2">
						<Button
							onClick={() => setFileFilter("all")}
							size="sm"
							variant={fileFilter === "all" ? "default" : "outline"}
						>
							All
						</Button>
						{(Object.keys(FILE_CATEGORY_CONFIG) as FileCategory[]).map(
							(cat) => {
								const config = FILE_CATEGORY_CONFIG[cat];
								const count =
									files?.filter((f) => f.category === cat).length || 0;
								if (count === 0) {
									return null;
								}
								return (
									<Button
										key={cat}
										onClick={() => setFileFilter(cat)}
										size="sm"
										variant={fileFilter === cat ? "default" : "outline"}
									>
										{config.label} ({count})
									</Button>
								);
							},
						)}
					</div>
					{/* Files Grid */}
					<FilesGrid
						files={filteredFiles}
						isLoading={filesLoading}
						onImageClick={setLightboxImage}
					/>
				</div>
			)}
			{/* Lightbox */}
			<Dialog
				onOpenChange={() => setLightboxImage(null)}
				open={!!lightboxImage}
			>
				<DialogContent className="max-h-[90vh] max-w-4xl overflow-hidden p-0">
					<DialogHeader className="absolute top-0 right-0 z-10 p-2">
						<button
							aria-label="Close image preview"
							className="rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
							onClick={() => setLightboxImage(null)}
							type="button"
						>
							<X className="h-5 w-5" />
						</button>
					</DialogHeader>
					{lightboxImage && (
						<img
							alt="Full size"
							className="h-auto max-h-[85vh] w-full object-contain"
							height={800}
							src={lightboxImage}
							width={1200}
						/>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}

type FileCardProps = {
	file: {
		id: string;
		fileName: string;
		fileUrl: string;
		thumbnailUrl?: string | null;
		mimeType: string;
		category: string;
		uploadedAt: string;
	};
	onImageClick: (url: string) => void;
};

function FileCard({ file, onImageClick }: FileCardProps) {
	const isImage = file.mimeType.startsWith("image/");
	const config =
		FILE_CATEGORY_CONFIG[file.category as FileCategory] ||
		FILE_CATEGORY_CONFIG.other;
	const Icon = config.icon;
	const thumbnailImage = (
		// biome-ignore lint/a11y/noNoninteractiveElementInteractions: onError handles image fallback.
		<img
			alt={file.fileName}
			className="h-full w-full object-cover transition-transform group-hover:scale-105"
			height={200}
			loading="lazy"
			onError={(e) => {
				e.currentTarget.style.display = "none";
			}}
			src={file.thumbnailUrl || file.fileUrl}
			width={200}
		/>
	);
	const thumbnail = isImage ? (
		<button
			className="h-full w-full"
			onClick={() => onImageClick(file.fileUrl)}
			type="button"
		>
			{thumbnailImage}
		</button>
	) : (
		<div className="flex h-full items-center justify-center">
			<Icon className="h-12 w-12 text-muted-foreground" />
		</div>
	);

	return (
		<div className="group relative overflow-hidden rounded-lg border bg-card">
			{/* Thumbnail */}
			<div className="aspect-square bg-muted">{thumbnail}</div>

			{/* Info */}
			<div className="p-3">
				<p className="truncate font-medium text-sm">{file.fileName}</p>
				<p className="text-muted-foreground text-xs">
					{new Date(file.uploadedAt).toLocaleDateString()}
				</p>
			</div>

			{/* Download Button */}
			<a
				className="absolute top-2 right-2 rounded-full bg-black/50 p-2 text-white opacity-0 transition-opacity hover:bg-black/70 group-hover:opacity-100"
				download={file.fileName}
				href={file.fileUrl}
			>
				<Download className="h-4 w-4" />
			</a>
		</div>
	);
}

function ProjectDetailSkeleton() {
	return (
		<div className="container mx-auto px-4 py-8">
			<Skeleton className="mb-6 h-4 w-32" />
			<Skeleton className="mb-2 h-8 w-64" />
			<Skeleton className="mb-8 h-4 w-48" />
			<div className="flex gap-3 overflow-x-auto">
				{[1, 2, 3, 4, 5].map((n) => (
					<Skeleton
						className="h-32 min-w-[200px] rounded-lg"
						key={`skeleton-${n}`}
					/>
				))}
			</div>
		</div>
	);
}

type FilesGridProps = {
	isLoading: boolean;
	files:
		| {
				id: string;
				fileName: string;
				fileUrl: string;
				thumbnailUrl?: string | null;
				mimeType: string;
				category: string;
				uploadedAt: string;
		  }[]
		| undefined;
	onImageClick: (url: string) => void;
};

function FilesGrid({ isLoading, files, onImageClick }: FilesGridProps) {
	if (isLoading) {
		return (
			<div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
				{[1, 2, 3, 4].map((n) => (
					<Skeleton
						className="aspect-square rounded-lg"
						key={`file-skeleton-${n}`}
					/>
				))}
			</div>
		);
	}

	if (files && files.length > 0) {
		return (
			<div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
				{files.map((file) => (
					<FileCard file={file} key={file.id} onImageClick={onImageClick} />
				))}
			</div>
		);
	}

	return (
		<div className="rounded-lg border bg-card p-12 text-center">
			<FolderOpen className="mx-auto h-12 w-12 text-muted-foreground" />
			<h3 className="mt-4 font-semibold text-lg">No files yet</h3>
			<p className="mt-2 text-muted-foreground">
				Files will appear here as your project progresses
			</p>
		</div>
	);
}
